// Build identity for the admin login footer. Reads the anonymous
// /api/admin/version alias (the only /api path Front Door routes on the admin
// host) and formats "v{version} · {sha7}". Never throws: any failure yields null
// so the footer simply shows no version text.

import { adminApiFetch } from './adminApiClient';

interface BuildInfoResponse {
  version?: unknown;
  commitSha?: unknown;
}

const SHORT_SHA_LENGTH = 7;

// BuildInfo (Web/Hosting/BuildInfo.cs) reports this literal when the assembly
// carries no informational version; it is not a real version, so show nothing.
const UNKNOWN_VERSION = 'unknown';

function nonEmpty(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/** Formats a /api/admin/version payload, or null when it carries no version. */
export function formatBuildVersion(info: unknown): string | null {
  if (typeof info !== 'object' || info === null) return null;
  const { version, commitSha } = info as BuildInfoResponse;

  const v = nonEmpty(version);
  if (!v || v.toLowerCase() === UNKNOWN_VERSION) return null;

  // commitSha is the server-validated git SHA (null when the build stamp carries none), so
  // the informationalVersion "+..." suffix is not re-parsed here: it may be non-SHA metadata.
  const sha = nonEmpty(commitSha);

  return sha ? `v${v} · ${sha.slice(0, SHORT_SHA_LENGTH)}` : `v${v}`;
}

export async function fetchBuildVersion(signal?: AbortSignal): Promise<string | null> {
  try {
    return formatBuildVersion(await adminApiFetch<unknown>('/api/admin/version', { signal }));
  } catch {
    return null;
  }
}
